import { Card, CardProps } from "@affinity/components/Card";
import { CTAGroup, CTAGroupField, CTAGroupProps } from "@affinity/components/CTAGroup";
import { FLOALA_TOOLBAR_COLLAPSED, FLOALA_TOOLBAR_EXPANDED, FROALA_CONFIG } from "@affinity/constants/froalaConfig";
import { rem } from "@affinity/constants/StyleHelpers";
import { RichContent } from "@affinity/Overrides/Widgets/Basic/RichContent";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import * as React from "react";
import styled from "styled-components";

const fields = {
    title: "title",
    content: "content",
    color: "color",
    icon: "icon",
    variant: "variant",
} as const;

interface Props extends WidgetProps {
    fields: {
        [fields.content]: string;
    } & CardProps &
        CTAGroupProps;
}

const CardWidget: React.FunctionComponent<Props> = ({
    fields: { color = "darkBlue", title, content, variant, icon, cta },
}) => {
    const richContentConfig = {
        content,
        padding: false,
    };

    return (
        <Card color={color} variant={variant} title={title} icon={icon}>
            <RichContent
                fields={richContentConfig}
                css={{}}
                zone=""
                isLayout={false}
                id=""
                parentId=""
                type=""
                generalFields={[]}
                translatableFields={{}}
                contextualFields={{}}
            />
            {cta && (
                <CardFooter>
                    <CTAGroup cta={cta} defaultProps={{ sizeVariant: "medium" }} />
                </CardFooter>
            )}
        </Card>
    );
};

const CardFooter = styled.div`
    margin-top: ${rem(10)};
`;

const widgetModule: WidgetModule = {
    component: CardWidget,
    definition: {
        group: "Basic",
        icon: "text",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.title,
                editorTemplate: "TextField",
                displayName: "Title",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.content,
                editorTemplate: "RichTextField",
                displayName: "Content",
                defaultValue: "",
                isRequired: true,
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                extendedConfig: {
                    ...FROALA_CONFIG,
                },
                collapsedToolbarButtons: FLOALA_TOOLBAR_COLLAPSED,
                expandedToolbarButtons: FLOALA_TOOLBAR_EXPANDED,
            },
            {
                fieldType: "General",
                name: fields.color,
                editorTemplate: "DropDownField",
                displayName: "Theme",
                defaultValue: "darkBlue",
                options: [
                    { displayName: "Turqoise", value: "darkBlue" },
                    { displayName: "Green", value: "green" },
                ],
                hideEmptyOption: true,
            },
            {
                fieldType: "General",
                name: fields.variant,
                editorTemplate: "DropDownField",
                displayName: "Card Style",
                defaultValue: "outline",
                options: [
                    { displayName: "Outline", value: "outline" },
                    { displayName: "Header", value: "header" },
                    { displayName: "Filled", value: "filled" },
                ],
            },
            {
                fieldType: "General",
                name: fields.icon,
                editorTemplate: "DropDownField",
                displayName: "Icon",
                defaultValue: "",
                options: [
                    { displayName: "No icon", value: "" },
                    { displayName: "Alert", value: "AlertCircle" },
                ],
                hideEmptyOption: true,
            },
            CTAGroupField,
        ],
    },
};

export default widgetModule;
